import { useState, useEffect, useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useAnnualBudget, BudgetItem } from '@/hooks/use-annual-budget';
import { FeatureGate } from '@/components/shared/FeatureGate';
import { useBudgetSync } from '@/hooks/use-budget-sync';
import { useRecurringTransactions } from '@/hooks/use-recurring-transactions';
import { updateRecurringSeriesAmount } from '@/lib/recurring-utils';
import { useToast } from '@/hooks/use-toast';
import { BudgetHeader } from '@/components/budget/BudgetHeader';
import { BudgetTable } from '@/components/budget/BudgetTable';
import { BudgetItemModal } from '@/components/budget/BudgetItemModal';
import { ProvisionDashboard } from '@/components/budget/ProvisionDashboard';
import { RecurrenceSyncDialog } from '@/components/budget/RecurrenceSyncDialog';

interface PendingSync {
  recurrenceId: string;
  oldAmount: number;
  newAmount: number;
}

export default function Budget() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<BudgetItem | null>(null);
  const [deleteItemId, setDeleteItemId] = useState<string | null>(null);
  const [pendingSync, setPendingSync] = useState<PendingSync | null>(null);

  const { budget, items, loading, createItem, updateItem, deleteItem } = useAnnualBudget(year);
  const { getActualAmount } = useBudgetSync(year, items);
  const { series: recurringSeries, refetch: refetchRecurring } = useRecurringTransactions();
  const { toast } = useToast();

  const currentMonth = new Date().getMonth() + 1;

  const { totalIncome, totalExpenses, totalActual } = useMemo(() => {
    let income = 0;
    let expenses = 0;
    let actual = 0;

    items.forEach(item => {
      item.active_months.forEach(month => {
        let monthAmount = 0;
        if (item.frequency === 'monthly') {
          monthAmount = item.monthly_amount;
        } else if (item.frequency === 'annual' && month === item.active_months[0]) {
          monthAmount = item.monthly_amount;
        }
        
        if (item.item_type === 'income') {
          income += monthAmount;
        } else {
          expenses += monthAmount;
        }
        
        // Actual amounts only for expenses
        if (item.item_type === 'expense') {
          actual += getActualAmount(item.category_id, month, item.recurrence_id);
        }
      });
    });

    return { totalIncome: income, totalExpenses: expenses, totalActual: actual };
  }, [items, getActualAmount]);

  const handleAddItem = () => {
    setEditingItem(null);
    setIsModalOpen(true);
  };

  const handleEditItem = (item: BudgetItem) => {
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleSaveItem = async (data: Partial<BudgetItem>) => {
    if (editingItem) {
      const result = await updateItem(editingItem.id, data);
      if (result?.needsRecurrenceSync && result.recurrenceId) {
        setPendingSync({
          recurrenceId: result.recurrenceId,
          oldAmount: editingItem.monthly_amount,
          newAmount: data.monthly_amount || 0,
        });
      }
    } else {
      await createItem(data);
      // If a recurring series was auto-created, refetch recurring data
      if ((data as any).auto_create_recurring) {
        await refetchRecurring();
      }
    }
  };

  const handleSyncConfirm = async () => {
    if (!pendingSync) return;
    try {
      // Find the series to get transaction IDs
      const targetSeries = recurringSeries.find(s => s.id === pendingSync.recurrenceId);
      if (targetSeries) {
        const count = await updateRecurringSeriesAmount(
          pendingSync.recurrenceId,
          pendingSync.newAmount,
          targetSeries.transactions
        );
        toast({
          title: 'Transações atualizadas',
          description: `${count} transação(ões) futura(s) atualizada(s) para ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(pendingSync.newAmount)}`,
        });
        await refetchRecurring();
      }
    } catch (error: any) {
      toast({
        title: 'Erro ao sincronizar',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setPendingSync(null);
    }
  };

  const handleSyncDecline = () => {
    setPendingSync(null);
  };

  const handleConfirmDelete = async () => {
    if (deleteItemId) {
      await deleteItem(deleteItemId);
      setDeleteItemId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <FeatureGate feature="budget_planning">
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <BudgetHeader
        year={year}
        onYearChange={setYear}
        budget={budget}
        totalIncome={totalIncome}
        totalExpenses={totalExpenses}
        totalActual={totalActual}
        onAddItem={handleAddItem}
      />

      <Tabs defaultValue="table">
        <TabsList>
          <TabsTrigger value="table">Visão Geral</TabsTrigger>
          <TabsTrigger value="provisions">Provisões</TabsTrigger>
        </TabsList>

        <TabsContent value="table" className="mt-4">
          {items.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground border rounded-lg">
              <p>Nenhum item cadastrado</p>
              <p className="text-sm mt-2">
                Clique em "Novo Item" para começar a planejar
              </p>
            </div>
          ) : (
            <BudgetTable
              items={items}
              getActualAmount={getActualAmount}
              onEditItem={handleEditItem}
              onDeleteItem={setDeleteItemId}
              currentMonth={currentMonth}
            />
          )}
        </TabsContent>

        <TabsContent value="provisions" className="mt-4">
          <ProvisionDashboard items={items} currentMonth={currentMonth} />
        </TabsContent>
      </Tabs>

      <BudgetItemModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        item={editingItem}
        onSave={handleSaveItem}
        recurringSeries={recurringSeries}
      />

      <AlertDialog open={!!deleteItemId} onOpenChange={() => setDeleteItemId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja remover este item do orçamento?
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete}>
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <RecurrenceSyncDialog
        open={!!pendingSync}
        onOpenChange={() => setPendingSync(null)}
        oldAmount={pendingSync?.oldAmount || 0}
        newAmount={pendingSync?.newAmount || 0}
        onConfirm={handleSyncConfirm}
        onDecline={handleSyncDecline}
      />
    </div>
    </FeatureGate>
  );
}
